import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { subscribeN4, when } from '../lib/n4';
import { Decode } from '../lib/fx';
import { EdgeCutover } from '../components/sites/EdgeCutover';
import { type EdgeState, certClass, daysLeft, errText, failOf, lastApplyOf, noteOf, pickList, pickText, portsText, toMs } from '../components/sites/common';

interface ImportRow { domain: string; ok: boolean; notes: string; site?: string; id?: number; kind?: string; conf?: string }
interface ImportRes { rows: ImportRow[]; skipped: { conf: string; reason: string }[]; created: { name: string; ok: boolean; id?: number | null; error?: string }[] | null; applyError: string }
type Busy = 'install' | 'apply' | 'import' | 'mode' | 'rollback' | null;

// Az import-jelentés: { report: [{ domain, name, conf, ok, kind, notes[] }], skipped: [{ conf, reason }], created?: [{ name, ok, id, error }], apply_error? }
function readImport(r: unknown): ImportRes {
  const o = (r ?? {}) as Record<string, unknown>;
  const created = Array.isArray(o.created) ? (o.created as ImportRes['created']) : null;
  const rows = readRows(r).map((x) => { const c = created?.find((y) => y.name === x.site); return c ? { ...x, id: c.id ?? x.id, ok: x.ok && c.ok, notes: c.ok ? x.notes : [c.error, x.notes].filter(Boolean).join(' · ') } : x; });
  return { rows, skipped: pickList<{ conf: string; reason: string }>(o.skipped), created, applyError: typeof o.apply_error === 'string' ? o.apply_error : '' };
}
function readRows(r: unknown): ImportRow[] {
  return pickList<Record<string, unknown>>(r, 'report', 'results', 'sites', 'items', 'rows').map((x) => {
    const notes = x.notes ?? x.note ?? x.warnings ?? x.error ?? '';
    return {
      domain: String(x.domain ?? (Array.isArray(x.domains) ? x.domains.join(', ') : x.domains ?? x.name ?? '?')),
      ok: x.ok !== undefined ? Boolean(x.ok) : !x.error,
      notes: Array.isArray(notes) ? notes.map(String).join(' · ') : String(notes ?? ''),
      site: typeof x.site === 'string' ? x.site : typeof x.name === 'string' ? x.name : undefined,
      id: typeof x.id === 'number' ? x.id : typeof x.site_id === 'number' ? x.site_id : undefined,
      kind: typeof x.kind === 'string' ? x.kind : undefined, conf: typeof x.conf === 'string' ? x.conf : undefined,
    };
  });
}

function Tile({ k, children, state }: { k: string; children: ReactNode; state?: 'ok' | 'bad' | 'warn' | '' }) {
  return <div className={`a-site-tile ${state ?? ''}`}><div className="k">{k}</div><div className="v">{children}</div></div>;
}

// Edge (Caddy) oldal: állapot, telepítés, alkalmazás, import az OpenResty-ből, mód, átállás + visszaút, tanúsítványok.
export function EdgePage() {
  const navigate = useNavigate();
  const [edge, setEdge] = useState<EdgeState | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [result, setResult] = useState<{ title: string; ok: boolean; text?: string; list?: string[] } | null>(null);
  const [report, setReport] = useState<ImportRes | null>(null);
  const [cutover, setCutover] = useState(false);

  const load = useCallback(() => { api<EdgeState>('/api/n4/edge').then((r) => { setEdge(r); setErr(''); }).catch((e) => setErr(errText(e, 'Edge-állapot'))); }, []);
  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20_000);
    const off = subscribeN4((ev) => { if (ev.type === 'edge') load(); });
    return () => { clearInterval(t); off(); };
  }, [load]);

  const call = async (b: Exclude<Busy, null>, path: string, body: unknown, title: string, after?: (r: unknown) => void) => {
    setBusy(b); setResult(null);
    try {
      const r = await api<unknown>(`/api/n4/edge/${path}`, { method: 'POST', body: JSON.stringify(body ?? {}) });
      failOf(r); // 200 + { ok:false, error } is kudarc
      const warnings = pickList<unknown>(r, 'warnings').map((w) => (typeof w === 'string' ? w : JSON.stringify(w)));
      setResult({ title, ok: true, text: [noteOf(r), pickText(r, 'output', 'log', 'message')].filter(Boolean).join('\n'), list: warnings });
      after?.(r);
    } catch (e) { setResult({ title, ok: false, text: errText(e) }); } finally { setBusy(null); load(); }
  };

  const mode = edge?.mode === 'prod' ? 'prod' : 'test';
  const toTest = () => { if (confirm('Az edge visszavált teszt-módba (8080/8443).\n\nHa az OpenResty most nem fut, a 80/443 elérhetetlen lesz! Teljes visszaúthoz (OpenResty indítása is) a „Visszaút” gombot használd.\n\nCsak a módot váltod?')) void call('mode', 'mode', { mode: 'test' }, 'Mód: teszt'); };
  const rollback = () => { if (confirm('Visszaút: az edge teszt-módba vált (8080/8443), az OpenResty újra elindul és visszaveszi a 80/443-at.\n\nFolytatod?')) void call('rollback', 'rollback-cutover', {}, 'Visszaút'); };
  const certs = [...(edge?.certs ?? [])].sort((a, b) => (toMs(a.expires) ?? 0) - (toMs(b.expires) ?? 0));
  const lastApply = lastApplyOf(edge?.last_apply);

  return (
    <div className="h-full overflow-auto">
      <div className="a-pg a-site-edge">
        <header className="a-pgh">
          <div className="min-w-0">
            <div className="a-crumbs"><a onClick={() => navigate('/sites')}>‹ Weboldalak</a><span>/</span></div>
            <h1 className="a-title"><Decode text="Edge" duration={600} /></h1>
            <p className="a-sub">A közös bejárat (Caddy): domain → weboldal, TLS magától. Teszt-módban a 8080/8443-on fut a mostani OpenResty mellett; élesre csak az <b>Átállás</b> visz.</p>
          </div>
        </header>
        {err && <div className="a-site-msg bad a-site-block">{err} <button className="a-btn sm" onClick={load}>Újra</button></div>}

        <div className="a-site-tiles a-stagger">
          <Tile k="Telepítve" state={edge ? (edge.installed ? 'ok' : 'warn') : ''}>{edge ? (edge.installed ? 'igen' : 'nem') : '—'}</Tile>
          <Tile k="Fut" state={edge?.installed ? (edge.running ? 'ok' : 'bad') : ''}>{edge ? <><i className={`a-sitelamp ${edge.running ? 'run' : 'stop'}`} /> {edge.running ? 'fut' : 'áll'}</> : '—'}</Tile>
          <Tile k="Mód" state={edge ? (mode === 'prod' ? 'ok' : 'warn') : ''}>{edge ? (mode === 'prod' ? 'ÉLES' : 'TESZT') : '—'}</Tile>
          <Tile k="Portok (http / https)">{edge ? portsText(edge.ports) : '—'}</Tile>
          <Tile k="ACME e-mail">{edge?.email || '—'}</Tile>
          <Tile k="Cloudflare-token" state={edge ? (edge.cf_token ? 'ok' : '') : ''}>{edge ? (edge.cf_token ? 'van (DNS-01)' : 'nincs (HTTP-01)') : '—'}</Tile>
          <Tile k="Weboldalak">{edge?.sites ?? '—'}</Tile>
          <Tile k="Utolsó alkalmazás" state={lastApply.ok === false ? 'bad' : lastApply.ok ? 'ok' : ''}><span title={lastApply.error || undefined}>{lastApply.at ? when(lastApply.at) : '—'}{lastApply.ok === false ? ' · hiba' : lastApply.warnings ? ` · ${lastApply.warnings} figy.` : ''}</span></Tile>
        </div>

        <div className="a-site-edgeacts">
          {edge && !edge.installed && <button className="a-btn solid" disabled={busy != null} onClick={() => void call('install', 'install', {}, 'Telepítés')}>{busy === 'install' ? 'Telepítés…' : 'Telepítés'}</button>}
          <button className="a-btn pri" disabled={busy != null || !edge?.installed} onClick={() => void call('apply', 'apply', {}, 'Alkalmazás')} title="A Caddyfile újragenerálása a nyilvántartásból, ellenőrzés, betöltés kiesés nélkül">{busy === 'apply' ? 'Alkalmazás…' : 'Alkalmazás'}</button>
          <button className="a-btn" disabled={busy != null} onClick={() => void call('import', 'import', { apply: false }, 'Import az OpenResty-ből (előnézet)', (r) => setReport(readImport(r)))} title="Előnézet: a meglévő OpenResty-vhostokból mi lenne site-sor (még semmi nem változik)">{busy === 'import' ? 'Import…' : 'Import az OpenResty-ből'}</button>
          <span className="a-site-modesw" title="Élesre csak az Átállás gombbal lehet váltani">
            <span className="lb">Mód</span>
            <span className="a-site-seg">
              <button type="button" className={mode === 'test' ? 'on' : ''} disabled={busy != null || !edge?.installed || mode === 'test'} onClick={toTest}>Teszt</button>
              <button type="button" className={mode === 'prod' ? 'on' : ''} disabled={busy != null || !edge?.installed || mode === 'prod'} onClick={() => setCutover(true)}>Éles</button>
            </span>
          </span>
          <span className="flex-1" />
          {mode === 'test' && <button className="a-btn solid a-site-cutbtn" disabled={busy != null || !edge?.installed} onClick={() => setCutover(true)}>Átállás…</button>}
          <button className="a-btn" disabled={busy != null || !edge?.installed} onClick={rollback} title="OpenResty vissza a 80/443-ra, edge teszt-módba">{busy === 'rollback' ? 'Visszaút…' : 'Visszaút'}</button>
        </div>

        {result && (
          <div className={`a-site-result ${result.ok ? 'ok' : 'bad'}`}>
            <div className="h"><b>{result.title}</b>: {result.ok ? 'kész' : 'nem sikerült'}<button type="button" onClick={() => setResult(null)} title="Bezárás">×</button></div>
            {result.text && <pre className="a-site-log short">{result.text}</pre>}
            {result.list && result.list.length > 0 && <><div className="a-site-note">Figyelmeztetések ({result.list.length}):</div><ul className="a-site-warnlist">{result.list.map((w, i) => <li key={i}>{w}</li>)}</ul></>}
            {result.ok && !result.text && !result.list?.length && result.title === 'Alkalmazás' && <div className="a-site-note">Figyelmeztetés nincs.</div>}
          </div>
        )}

        {report && (
          <section className="a-site-sec">
            <h3>Import-jelentés <span className="a-chip">{report.rows.filter((r) => r.ok).length} / {report.rows.length} rendben</span>
              {!report.created && report.rows.length > 0 && <button className="a-btn sm solid" style={{ marginLeft: 'auto' }} disabled={busy != null}
                onClick={() => { if (confirm(`Felveszed a(z) ${report.rows.length} oldalat a Weboldalak közé?\n\nA statikus gyökerek a data/sites/<név>/www alá másolódnak; a régi OpenResty-bejárat változatlanul fut tovább.`)) void call('import', 'import', { apply: true }, 'Import: felvétel a nyilvántartásba', (r) => setReport(readImport(r))); }}>Felvétel a nyilvántartásba</button>}</h3>
            {report.applyError && <div className="a-site-msg bad">{report.applyError}</div>}
            {report.created && <div className="a-site-msg ok">Felvéve: {report.created.filter((c) => c.ok).length} / {report.created.length} oldal.</div>}
            <div className="a-site-tablewrap">
              <table className="a-site-table">
                <thead><tr><th>Domain</th><th className="c">OK</th><th>Megjegyzés</th></tr></thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={i} className={r.ok ? '' : 'warnrow'} onClick={() => { if (r.id != null) navigate(`/sites/${r.id}`); }} style={{ cursor: r.id != null ? 'pointer' : undefined }}>
                      <td className="mono">{r.domain}<div className="sub">{[r.site !== r.domain ? r.site : '', r.kind, r.conf].filter(Boolean).join(' · ')}</div></td>
                      <td className={`c ${r.ok ? 'okc' : 'badc'}`}>{r.ok ? '✓' : '✗'}</td>
                      <td>{r.notes || <span className="mut">—</span>}</td>
                    </tr>
                  ))}
                  {report.rows.length === 0 && <tr className="empty"><td colSpan={3}>Az import nem talált vhostot.</td></tr>}
                </tbody>
              </table>
            </div>
            {report.skipped.length > 0 && <><div className="a-site-note" style={{ marginTop: 10 }}>Kihagyva ({report.skipped.length}):</div><ul className="a-site-warnlist">{report.skipped.map((s, i) => <li key={i}><code>{s.conf}</code> — {s.reason}</li>)}</ul></>}
            <p className="a-site-note">{report.created ? <>A létrejött oldalak a <Link to="/sites" className="a-site-link">Weboldalak</Link> listában; a nem fordítható részek a site jegyzetébe kerültek.</> : 'Ez előnézet: még semmi nem változott. A „Felvétel a nyilvántartásba” veszi fel az oldalakat (a régi bejárat közben változatlanul fut).'}</p>
          </section>
        )}

        <section className="a-site-sec">
          <h3>Tanúsítványok</h3>
          {certs.length ? (
            <div className="a-site-tablewrap">
              <table className="a-site-table">
                <thead><tr><th>Domain</th><th>Lejárat</th><th className="num">Hátra</th></tr></thead>
                <tbody>
                  {certs.map((c) => { const d = daysLeft(c.expires); const t = toMs(c.expires); return (
                    <tr key={c.domain} className={`cert ${certClass(d)}`}>
                      <td className="mono">{c.domain}</td>
                      <td className="tm">{t ? new Date(t).toLocaleDateString('hu-HU') : '—'}</td>
                      <td className={`num a-site-cert ${certClass(d)}`}>{d == null ? '—' : d < 0 ? 'LEJÁRT' : `${d} nap`}</td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          ) : <p className="a-site-note">{edge ? 'Még nincs tanúsítvány (teszt-módban a belső CA ad, élesben az ACME).' : '—'}</p>}
        </section>
      </div>
      {cutover && edge && <EdgeCutover edge={edge} onClose={() => setCutover(false)} onDone={(text) => { setCutover(false); setResult({ title: 'Átállás', ok: true, text }); load(); }} />}
    </div>
  );
}
